import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { RiskRegistry } from '@webapp/risk-core';
import { StoreService } from '@webapp/core/services/store.service';
import { IRisk } from '../interfaces/quoter.interface';
import { IRiskFlowConfig, IRiskFlowOption } from '../interfaces/risk-flow-config';
import { QuoterService } from '../services/quoter.service';
import { QUOTER_RISK_SLOTS } from '../config/provide-quoter-risk-ui';

export interface LoadRisksOptions {
  /**
   * When true, filters the fetched risk list down to only the risk tags that
   * contribute a mobile `quoter-flow` slot (mobile's use case — show only
   * implemented flows). When absent/false, the full unfiltered list is kept
   * (apps/agents' show-all-risks behavior).
   */
  filterToRegistered?: boolean;
  /**
   * When true, opts out of the global HttpErrorInterceptor toast because the
   * caller renders its own inline (retryable) error state (mobile's quoter
   * page). Absent/false keeps the toast (apps/agents).
   */
  skipErrorToast?: boolean;
}

interface RiskListState {
  risks: IRisk[];
  options: IRiskFlowOption[];
  loading: boolean;
  error: string | null;
}

@Injectable({ providedIn: 'root' })
export class RiskListStore extends StoreService<RiskListState> {
  private readonly quoterService = inject(QuoterService);
  private readonly riskRegistry = inject(RiskRegistry);

  readonly risks = this.select('risks');
  readonly options = this.select('options');
  readonly loading = this.select('loading');
  readonly error = this.select('error');

  constructor() {
    super();
    this.setState({ risks: [], options: [], loading: false, error: null });
  }

  async loadRisks(opts?: LoadRisksOptions): Promise<void> {
    this.setState({ loading: true, error: null });
    try {
      const requestOptions = opts?.skipErrorToast ? { skipErrorToast: true } : undefined;
      const risks = await firstValueFrom(this.quoterService.getRisks(requestOptions));

      if (opts?.filterToRegistered) {
        const options = (await Promise.all(risks.map(risk => this.toFlowOption(risk)))).filter(
          (o): o is IRiskFlowOption => o !== null
        );
        this.setState({ risks: options.map(o => o.risk), options, loading: false });
        return;
      }

      this.setState({ risks, options: [], loading: false });
    } catch {
      this.setState({
        risks: [],
        options: [],
        loading: false,
        error: 'No se pudieron cargar los riesgos. Intentá de nuevo.',
      });
    }
  }

  /**
   * A risk is offered on mobile when its plugin does not opt out and it has a
   * `quoter-flow` slot; the slot loads the flow config shown on the card.
   */
  private async toFlowOption(risk: IRisk): Promise<IRiskFlowOption | null> {
    if (this.riskRegistry.find(risk.tag)?.capabilities.mobile === false) return null;
    const slot = this.riskRegistry.slot('mobile', risk.tag, QUOTER_RISK_SLOTS.QUOTER_FLOW);
    if (!slot) return null;
    return { risk, config: (await slot.load()) as IRiskFlowConfig };
  }
}
